import React, { useState, useEffect } from "react";
import { Button, Card, Form, Row, Col } from "react-bootstrap";
import Spinner from "components/Spinner";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { formatPhoneNumber } from "services/UtilityService";
import "./CreateCustomerForm.css";

const CreateCustomerForm = ({ onSubmit, onCancel }) => {
  const [customerName, setCustomerName] = useState("");
  const [programName, setProgramName] = useState("");
  const [customerNameAbbreviation, setCustomerNameAbbreviation] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [supportNumber, setSupportNumber] = useState("");
  const [faxNumberInbound, setFaxNumberInbound] = useState("");
  const [email, setEmail] = useState("");
  const [isAutoAcceptEnabled, setIsAutoAcceptEnabled] = useState(false);
  const [isIntroFaxEnabled, setIsIntroFaxEnabled] = useState(false);
  const [showSavingPrices, setShowSavingPrices] = useState(true);
  const [polypharmacyEnabled, setPolypharmacyEnabled] = useState(false);
  const [deprescriptionConfirmationEnabled, setDeprescriptionConfirmationEnabled] = useState(false);
  const [botCallingAllowed, setBotCallingAllowed] = useState(false);
  const [botEntityName, setBotEntityName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Reset form state when the component unmounts or re-mounts (e.g. modal close/open)
    return () => {
       setCustomerName("");
       setProgramName("");
       setCustomerNameAbbreviation("");
       setLogoUrl("");
       setSupportNumber("");
       setFaxNumberInbound("");
      setEmail("");
      setIsAutoAcceptEnabled(false);
      setIsIntroFaxEnabled(false);
      setShowSavingPrices(true);
      setPolypharmacyEnabled(false);
      setDeprescriptionConfirmationEnabled(false);
      setBotCallingAllowed(false);
      setBotEntityName("");
      setLoading(false);
    }
  }, []);

  const handleSubmit = () => {
    setLoading(true);
    const customerData = {
      customerName,
      programName,
      customerNameAbbreviation,
      logoUrl,
      supportNumber,
      faxNumberInbound,
      email,
      isAutoAcceptEnabled,
      isIntroFaxEnabled,
      showSavingPrices,
      polypharmacyEnabled,
      deprescriptionConfirmationEnabled,
      botCallingAllowed,
      botEntityName,
    };
    onSubmit(customerData, () => setLoading(false));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Form>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Identity</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="e.g. Centene"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={programName}
                    onChange={(e) => setProgramName(e.target.value)}
                    placeholder="e.g. Centene RX Savings"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Short Code <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={customerNameAbbreviation}
                    onChange={(e) => setCustomerNameAbbreviation(e.target.value)}
                    placeholder="e.g. CNT"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="e.g. mcs-logo.png"
                  />
                  <p className="field-sub-text">Logo Name in the S3 bucket</p>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Contact</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Support Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatPhoneNumber(supportNumber)}
                    onChange={(e) => setSupportNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                    placeholder="e.g. (555) 555-5555"
                    maxLength={14}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound Fax Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatPhoneNumber(faxNumberInbound)}
                    onChange={(e) => setFaxNumberInbound(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                    placeholder="e.g. (555) 555-5555"
                    maxLength={14}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ops@example.com"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Programs</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="auto-accept-switch"
                    label="Auto Accept"
                    checked={isAutoAcceptEnabled}
                    onChange={(e) => setIsAutoAcceptEnabled(e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="intro-fax-switch"
                    label="Intro Fax"
                    checked={isIntroFaxEnabled}
                    onChange={(e) => setIsIntroFaxEnabled(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="polypharmacy-switch"
                    label="Polypharmacy"
                    checked={polypharmacyEnabled}
                    onChange={(e) => setPolypharmacyEnabled(e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="deprescription-confirmation-switch"
                    label="Deprescription Confirmations"
                    checked={deprescriptionConfirmationEnabled}
                    onChange={(e) => setDeprescriptionConfirmationEnabled(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Automated Calling</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="bot-calling-allowed-switch"
                    label="Bot Calling"
                    checked={botCallingAllowed}
                    onChange={(e) => setBotCallingAllowed(e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="d-flex align-items-center">
                  <Form.Label htmlFor="bot-entity-name" className="mb-0 mr-3 flex-shrink-0">
                    Name of Bot Caller
                  </Form.Label>
                  <Form.Control
                    id="bot-entity-name"
                    className="flex-grow-1"
                    type="text"
                    value={botEntityName}
                    onChange={(e) => setBotEntityName(e.target.value)}
                    placeholder="e.g. Acme Bot"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Card.Title as="h6">Pricing</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="show-saving-prices-switch"
                    label="Show Prices"
                    checked={showSavingPrices}
                    onChange={(e) => setShowSavingPrices(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>
      <div className="d-flex justify-content-end mt-3">
        <Button variant="info" onClick={onCancel} className="mr-2">
          Cancel
        </Button>
        <Button
          variant="btn btn-primary"
          onClick={handleSubmit}
          disabled={!customerName || !programName || !customerNameAbbreviation}
        >
          Create Customer
        </Button>
      </div>
    </>
  );
};

export default CreateCustomerForm;